import { supabase } from "../lib/supabaseClient";
import {
    ScanAccreditationRequest,
    ScanAccreditationResponse,
    BadgeTemplate,
    UpsertBadgeTemplatePayload,
    GenerateBadgeResponse,
    MediaPersonnel,
} from "../types/media";

// ============================================================================
// NAME / CONTACT FALLBACK (account-less imported personnel have no profile row)
// ============================================================================

/**
 * Resolve a personnel display name. Imported journalists have `profile_id = NULL`,
 * so fall back to the denormalized `first_name` / `last_name` (and `_ar` in RTL).
 */
export function getPersonnelName(
    personnel: Pick<
        MediaPersonnel,
        | "profile"
        | "first_name"
        | "last_name"
        | "first_name_ar"
        | "last_name_ar"
    >,
    arabic = false
): string {
    if (personnel.profile?.full_name) return personnel.profile.full_name;
    const first = arabic
        ? personnel.first_name_ar || personnel.first_name
        : personnel.first_name || personnel.first_name_ar;
    const last = arabic
        ? personnel.last_name_ar || personnel.last_name
        : personnel.last_name || personnel.last_name_ar;
    return [first, last].filter(Boolean).join(" ").trim();
}

/**
 * Resolve a personnel email, falling back to the denormalized column.
 */
export function getPersonnelEmail(
    personnel: Pick<MediaPersonnel, "profile" | "email">
): string | null {
    return personnel.profile?.email || personnel.email || null;
}

/**
 * Resolve a personnel phone, falling back to the denormalized column.
 */
export function getPersonnelPhone(
    personnel: Pick<MediaPersonnel, "profile" | "phone">
): string | null {
    return personnel.profile?.phone || personnel.phone || null;
}

/**
 * Media Service
 * Handles API calls to Supabase and Edge Functions for media accreditation
 */

// ============================================================================
// QR CODE SCANNING OPERATIONS (PUBLIC - NO AUTH)
// ============================================================================

/**
 * Scan and verify an accreditation QR token
 * This is called from the public verification page (no auth required)
 */
/** Drops unset / blank optional fields of a scan request. */
export function compactScanRequest(request: ScanAccreditationRequest): ScanAccreditationRequest {
    const out: ScanAccreditationRequest = { token: request.token };
    if (request.match_id) out.match_id = request.match_id;
    if (request.zone_type) out.zone_type = request.zone_type;
    if (request.gate_location?.trim()) out.gate_location = request.gate_location.trim();
    if (request.device_id?.trim()) out.device_id = request.device_id.trim();
    return out;
}

export async function scanAccreditation(
    request: ScanAccreditationRequest
): Promise<ScanAccreditationResponse> {
    // The page is public, but a gate agent signed in to the webapp on the same
    // phone must be recognised: scan-accreditation records an ENTRY only for
    // gate staff / the match delegate, and a mere verification (public_link,
    // no token usage) for everyone else. Anonymous visitors keep the anon key.
    const { data: sessionData } = await supabase.auth.getSession().catch(() => ({
        data: { session: null },
    }));
    const bearer =
        sessionData?.session?.access_token ?? import.meta.env.VITE_SUPABASE_ANON_KEY;
    const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scan-accreditation`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
                Authorization: `Bearer ${bearer}`,
            },
            // Only the fields actually set: the function treats an empty
            // gate_location / match_id as absent anyway, but a scan from the
            // public page must not claim a checkpoint it does not have.
            body: JSON.stringify(compactScanRequest(request)),
        }
    );

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get the press card image URL from Supabase Storage
 */
/**
 * Get the press card image URL from Supabase Storage (authenticated)
 */
export function getPressCardUrl(pressCardPath: string | null): string | null {
    if (!pressCardPath) return null;

    // If it's already a full URL, check if it's public and convert to authenticated
    if (pressCardPath.startsWith("http")) {
        if (pressCardPath.includes('/object/public/')) {
            return pressCardPath.replace('/object/public/', '/object/authenticated/');
        }
        return pressCardPath;
    }

    // Otherwise, construct the authenticated storage URL
    const { data } = supabase.storage
        .from("media-documents")
        .getPublicUrl(pressCardPath);

    // Convert public URL to authenticated URL
    return data.publicUrl.replace('/object/public/', '/object/authenticated/');
}

/**
 * Get the ONJSA press-card URL (professional personnel). Same private
 * media-documents bucket / URL-conversion logic as `getPressCardUrl`.
 */
export function getOnjsaCardUrl(onjsaCardPath: string | null | undefined): string | null {
    return getPressCardUrl(onjsaCardPath ?? null);
}

/**
 * Get the club President attestation URL (club_correspondent personnel).
 * Same private media-documents bucket / URL-conversion logic as `getPressCardUrl`.
 */
export function getClubAttestationUrl(clubAttestationPath: string | null | undefined): string | null {
    return getPressCardUrl(clubAttestationPath ?? null);
}

/**
 * Get the identity-document URL (self-declared identity doc). Same private
 * media-documents bucket / URL-conversion logic as `getPressCardUrl`.
 */
export function getIdCardUrl(idCardPath: string | null | undefined): string | null {
    return getPressCardUrl(idCardPath ?? null);
}

/**
 * Get the agreement / accreditation-letter URL. Same private
 * media-documents bucket / URL-conversion logic as `getPressCardUrl`.
 */
export function getAgreementUrl(agreementPath: string | null | undefined): string | null {
    return getPressCardUrl(agreementPath ?? null);
}

/**
 * Get the media personnel photo URL (authenticated)
 */
export function getMediaPhotoUrl(photoPath: string | null): string | null {
    if (!photoPath) return null;

    if (photoPath.startsWith("http")) {
        if (photoPath.includes('/object/public/')) {
            return photoPath.replace('/object/public/', '/object/authenticated/');
        }
        return photoPath;
    }

    const { data } = supabase.storage
        .from("media-documents")
        .getPublicUrl(photoPath);

    // Convert public URL to authenticated URL
    return data.publicUrl.replace('/object/public/', '/object/authenticated/');
}

/**
 * Fetch an authenticated image and return as blob URL
 * Use this for displaying images in <img> tags with auth
 */
export async function fetchAuthenticatedImage(url: string): Promise<string | null> {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            console.error('No auth session for image fetch');
            return null;
        }

        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    } catch (error) {
        console.error('Failed to fetch authenticated image:', error);
        return null;
    }
}

// ============================================================================
// BADGE TEMPLATE OPERATIONS
// ============================================================================

/**
 * Fetch badge templates for a league (optionally scoped to a season).
 * Returns season-specific templates plus league-wide (season = null) fallbacks.
 */
export async function getBadgeTemplates(
    leagueId: string,
    season?: string | null
): Promise<BadgeTemplate[]> {
    let query = supabase
        .from("league_badge_templates")
        .select("*")
        .eq("league_id", leagueId)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });

    if (season) {
        query = query.or(`season.eq.${season},season.is.null`);
    }

    const { data, error } = await query;

    if (error) {
        throw new Error(`Failed to load badge templates: ${error.message}`);
    }

    return (data as BadgeTemplate[]) || [];
}

/**
 * Create or update a badge template. When `id` is provided, updates in place;
 * otherwise inserts a new template.
 */
export async function upsertBadgeTemplate(
    payload: UpsertBadgeTemplatePayload
): Promise<BadgeTemplate> {
    const record = {
        league_id: payload.league_id,
        season: payload.season ?? null,
        name: payload.name,
        is_default: payload.is_default ?? false,
        is_active: payload.is_active ?? true,
        config: payload.config,
        updated_at: new Date().toISOString(),
    };

    const mutation = payload.id
        ? supabase
              .from("league_badge_templates")
              .update(record)
              .eq("id", payload.id)
              .select("*")
              .single()
        : supabase
              .from("league_badge_templates")
              .insert(record)
              .select("*")
              .single();

    const { data, error } = await mutation;

    if (error) {
        throw new Error(`Failed to save badge template: ${error.message}`);
    }

    return data as BadgeTemplate;
}

/**
 * Trigger server-side badge (PDF) generation for an approved accreditation.
 * Returns a short-lived signed URL to the generated PDF.
 */
export async function generateAccreditationBadge(
    accreditationId: string | null,
    lang?: "fr" | "en" | "ar",
    opts?: { preview?: boolean; leagueId?: string | null; config?: Record<string, unknown> }
): Promise<GenerateBadgeResponse> {
    const { data, error } = await supabase.functions.invoke(
        "generate-accreditation-badge",
        {
            body: {
                ...(accreditationId ? { accreditation_id: accreditationId } : {}),
                ...(lang ? { lang } : {}),
                ...(opts?.preview ? { preview: true } : {}),
                ...(opts?.leagueId ? { league_id: opts.leagueId } : {}),
                ...(opts?.config ? { config: opts.config } : {}),
            },
        }
    );

    if (error) {
        throw new Error(`Failed to generate badge: ${error.message}`);
    }

    return data as GenerateBadgeResponse;
}

export interface LeagueBrandingAssets {
    logo: string | null;
    background: string | null;
    stamp: string | null;
}

/**
 * Read the league's active visual-identity assets ("Identité visuelle" /
 * "Assets graphiques") from league_settings_versions so the badge designer
 * can reuse them instead of re-uploading logos.
 */
export async function getLeagueBranding(
    leagueId: string
): Promise<LeagueBrandingAssets> {
    const { data, error } = await supabase
        .from("league_settings_versions")
        .select("config")
        .eq("league_id", leagueId)
        .eq("is_active", true)
        .order("version", { ascending: false })
        .limit(1)
        .maybeSingle();

    if (error) {
        throw new Error(`Failed to load league branding: ${error.message}`);
    }

    const imgs = (data as any)?.config?.missionOrder?.imageUrls || {};
    return {
        logo: imgs.logo || null,
        background: imgs.background || null,
        stamp: imgs.stamp || null,
    };
}
